import React, { FC, ReactNode, DetailedHTMLProps } from "react";
import { IFieldProps } from "./IFieldProps";
import { Box, Typography, Theme, Tooltip } from "@material-ui/core";
import "./coloredStatusField.css";
import { useTheme } from "@material-ui/styles";
import { useSelector } from "react-redux";
import { AppState, ThemeName } from "../../../common/types";
import { IColoredFieldProps } from "./ColoredField";
import { sanitizeFieldProps } from "../../../common/util/sanitizeFieldProps";
import { useTranslate, Translate } from "react-admin";
import { isString } from "lodash";

interface IColoredStatusFieldProps extends IColoredFieldProps {
  labelStyle: "singleLabel" | "rows" | "columns";
  applyColorTo: "everything" | "first" | "second";
  secondLabelSource?: string;
  renderComponent?: FC<IFieldProps>;
  secondLabelRenderComponent?: FC<IFieldProps>;
  tooltip?: string;
  labelTranslationNamespace?: string;
  headerClassName?: string;
  className?: string;
}

const OuterBox = (
  props: IColoredStatusFieldProps & { children: ReactNode } & Omit<
      DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
      "color"
    >
) => {
  const theme: Theme = useTheme();
  const lightMode = useSelector((state: AppState) => state.theme);
  const translate = useTranslate();

  const {
    labelStyle,
    applyColorTo,
    record,
    source,
    backgroundColor: getBackgroundColor,
    color,
    children,
    className,
    tooltip,
    labelTranslationNamespace,
    ...rest
  } = props;

  let flexDirection: string;
  switch (labelStyle) {
    case "singleLabel":
    case "rows":
      flexDirection = "row";
      break;
    case "columns":
      flexDirection = "column";
  }

  const statusFieldBox = (
    <Box
      className={`colored-status-field-outer centered-content ${className}`}
      flexDirection={flexDirection}
      style={{
        backgroundColor:
          applyColorTo === "everything" && record
            ? isString(getBackgroundColor)
              ? getBackgroundColor
              : getBackgroundColor(record, source, lightMode === "dark")
            : "auto",
        borderColor:
          applyColorTo === "everything" ? theme.palette.divider : "auto",
        borderWidth: applyColorTo === "everything" ? "1px" : "auto",
        borderStyle: applyColorTo === "everything" ? "solid" : "auto",
      }}
      {...sanitizeFieldProps(rest)}>
      {children}
    </Box>
  );

  return tooltip ? (
    <Tooltip title={translate(tooltip, { _: tooltip })}>
      {statusFieldBox}
    </Tooltip>
  ) : (
    statusFieldBox
  );
};

const getBoxWithSingleLabel = (
  props: IColoredStatusFieldProps,
  labelSource: any,
  lightMode: ThemeName,
  translate: Translate
) => {
  const {
    renderComponent: RenderComponent,
    ...propsWithoutRenderComponent
  } = props;

  const {
    record,
    source,
    backgroundColor,
    color,
    labelTranslationNamespace,
    ...rest
  } = propsWithoutRenderComponent;

  return (
    <OuterBox {...props}>
      <Box
        className="colored-status-field-child"
        style={{
          backgroundColor: record
            ? isString(backgroundColor)
              ? backgroundColor
              : backgroundColor(record, source, lightMode === "dark")
            : "auto",
          color: record
            ? isString(color)
              ? color
              : color(record, source, lightMode === "dark")
            : "auto",
        }}>
        {typeof RenderComponent !== "undefined" ? (
          <RenderComponent {...propsWithoutRenderComponent}></RenderComponent>
        ) : (
          <Box {...sanitizeFieldProps(rest)}>
            <Typography variant="body2">
              {translate(labelSource, { _: labelSource })}
            </Typography>
          </Box>
        )}
      </Box>
    </OuterBox>
  );
};

const ColoredStatusField: FC<IColoredStatusFieldProps> = (
  props: IColoredStatusFieldProps &
    Omit<
      DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
      "color"
    >
) => {
  const theme = useSelector((state: AppState) => state.theme);
  const translate = useTranslate();

  const {
    record,
    source,
    labelStyle,
    secondLabelSource,
    secondLabelRenderComponent,
    color: getColor,
    backgroundColor: getBackgroundColor,
    labelTranslationNamespace,
    ...rest
  } = props;

  if (!record || !source) {
    return null;
  }

  if (!record[source]) {
    return null;
  }

  const labelSource = record[source];
  const color = isString(getColor)
    ? getColor
    : getColor(record, source, theme === "dark");
  const backgroundColor = isString(getBackgroundColor)
    ? getBackgroundColor
    : getBackgroundColor(record, source, theme === "dark");
  switch (labelStyle) {
    case "columns":
    case "rows":
      if (!secondLabelSource) {
        return getBoxWithSingleLabel(
          props,
          !!labelTranslationNamespace
            ? `${labelTranslationNamespace}.${labelSource}`
            : labelSource,
          theme,
          translate
        );
      }

      if (!record[secondLabelSource]) {
        return getBoxWithSingleLabel(
          props,
          !!labelTranslationNamespace
            ? `${labelTranslationNamespace}.${labelSource}`
            : labelSource,
          theme,
          translate
        );
      }

      const secondLabel = record[secondLabelSource];
      let secondRenderComponentProps = { ...props };
      if (typeof secondLabelRenderComponent !== "undefined") {
        secondRenderComponentProps = {
          ...props,
          source: secondLabelSource,
        };
      }

      return (
        <OuterBox {...props}>
          <Box
            className="colored-status-field-child"
            style={{
              color:
                props.applyColorTo === "everything" ||
                props.applyColorTo === "first"
                  ? color
                  : "inherit",
              backgroundColor:
                props.applyColorTo === "everything" ||
                props.applyColorTo === "first"
                  ? backgroundColor
                  : "inherit",
            }}>
            {typeof props.renderComponent !== "undefined" ? (
              <props.renderComponent {...props}></props.renderComponent>
            ) : (
              <Box {...sanitizeFieldProps(rest)}>
                <Typography variant="body2">
                  {!!labelTranslationNamespace
                    ? translate(`${labelTranslationNamespace}.${labelSource}`, {
                        _: `${labelTranslationNamespace}.${labelSource}`,
                      })
                    : translate(labelSource, { _: labelSource })}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            className="colored-status-field-child"
            style={{
              color:
                props.applyColorTo === "everything" ||
                props.applyColorTo === "second"
                  ? color
                  : "inherit",
              backgroundColor:
                props.applyColorTo === "everything" ||
                props.applyColorTo === "second"
                  ? backgroundColor
                  : "inherit",
            }}>
            {typeof props.secondLabelRenderComponent !== "undefined" ? (
              <props.secondLabelRenderComponent
                {...secondRenderComponentProps}></props.secondLabelRenderComponent>
            ) : (
              <Box {...sanitizeFieldProps(rest)}>
                <Typography variant="body2">
                  {translate(secondLabel, { _: secondLabel })}
                </Typography>
              </Box>
            )}
          </Box>
        </OuterBox>
      );

    case "singleLabel":
      return getBoxWithSingleLabel(props, labelSource, theme, translate);
  }
};

export default ColoredStatusField;
